import { supabase } from '@/lib/supabase'
import type { TF } from '../types'
import type { OhlcvRow } from './candlesService'
import { fetchCandles } from './candlesService'

// Busca candles fechados e, quando disponível, agrega o candle vigente a partir de 5m
export async function fetchCandlesLive(params: {
  symbol: string
  timeframe: TF
  limit?: number
}): Promise<OhlcvRow[]> {
  let base = await fetchCandles(params)
  const symbol = String(params.symbol || '').trim().toUpperCase()
  const timeframe = String(params.timeframe || '').trim().toUpperCase() as TF

  try {
    // Auto-heal 1: se não houver candles retornados (lacuna inesperada), aciona runner e refaz uma vez
    if ((!base || base.length === 0) && symbol && timeframe) {
      try {
        const supabaseUrl =
          (import.meta as any).env?.VITE_SUPABASE_URL ||
          (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_URL ||
          (import.meta as any).env?.SUPABASE_URL
        const anon =
          (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
          (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
          (import.meta as any).env?.SUPABASE_ANON_KEY
        if (supabaseUrl && anon) {
          const u = `${String(supabaseUrl).replace(
            /\/$/,
            '',
          )}/functions/v1/ohlcv-queue-runner?symbol=${encodeURIComponent(
            symbol,
          )}&tf=${encodeURIComponent(timeframe)}`
          await fetch(u, {
            method: 'POST',
            headers: { Authorization: `Bearer ${anon}` },
          }).catch(() => {})
          await new Promise((r) => setTimeout(r, 600))
          base = await fetchCandles(params)
        }
      } catch {
        // silencioso; fallback usa base original
      }
    }

    // Auto-heal 2: se houver candles mas o último fechado esperado não existe, tenta derivar e refaz
    if (Array.isArray(base) && base.length > 0 && symbol && timeframe) {
      const lastIso = String(base[base.length - 1]?.open_time || '')
      const lastMs = Date.parse(lastIso)
      if (Number.isFinite(lastMs)) {
        const now = Date.now()
        const stepMs = (() => {
          switch (timeframe) {
            case '5':
              return 5 * 60 * 1000
            case '15':
              return 15 * 60 * 1000
            case '30':
              return 30 * 60 * 1000
            case '60':
              return 60 * 60 * 1000
            case '240':
              return 240 * 60 * 1000
            case '720':
              return 720 * 60 * 1000
            case 'D':
              return 24 * 60 * 60 * 1000
            case 'W':
              return 7 * 24 * 60 * 60 * 1000
            case 'M':
            default:
              return 30 * 24 * 60 * 60 * 1000
          }
        })()

        const floorNow = Math.floor(now / stepMs) * stepMs
        const lastClosed = floorNow - stepMs

        if (
          lastMs < lastClosed - stepMs &&
          ['240', '720', 'D', 'W', 'M'].includes(timeframe)
        ) {
          try {
            const supabaseUrl =
              (import.meta as any).env?.VITE_SUPABASE_URL ||
              (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_URL ||
              (import.meta as any).env?.SUPABASE_URL
            const anon =
              (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
              (import.meta as any).env?.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
              (import.meta as any).env?.SUPABASE_ANON_KEY
            if (supabaseUrl && anon) {
              const u = `${String(supabaseUrl).replace(
                /\/$/,
                '',
              )}/functions/v1/ohlcv-queue-runner?symbol=${encodeURIComponent(
                symbol,
              )}&tf=${encodeURIComponent(timeframe)}`
              await fetch(u, {
                method: 'POST',
                headers: { Authorization: `Bearer ${anon}` },
              }).catch(() => {})
              await new Promise((r) => setTimeout(r, 800))
              base = await fetchCandles(params)
            }
          } catch {
            // silencioso; fallback usa base original
          }
        }
      }
    }

    if (!symbol) return base

    // Agrega o candle vigente a partir de 5m e garante que o gráfico
    // use sempre o preço mais recente do bucket atual para todos os TFs,
    // substituindo o último candle se ele já existir ou anexando se for um bucket novo.
    const { data: cur, error: curErr } = await supabase.rpc(
      'current_bucket_from_5m',
      {
        p_symbol: symbol,
        p_tf: timeframe,
      },
    )

    if (!curErr && cur && typeof cur === 'object') {
      const openTime = (cur as any).open_time as string | null
      const progress = Number((cur as any).progress_pct ?? 0)
      const open = Number((cur as any).open)
      const high = Number((cur as any).high)
      const low = Number((cur as any).low)
      const close = Number((cur as any).close)
      const vol =
        (cur as any).volume == null ? null : Number((cur as any).volume)

      // Apenas aplica quando temos bucket consistente (open/high/low/close válidos).
      if (
        openTime &&
        progress >= 0 &&
        [open, high, low, close].every(Number.isFinite)
      ) {
        const idx = base.findIndex(
          (r) => String(r.open_time) === String(openTime),
        )

        if (idx >= 0) {
          // Substitui o candle existente pelo bucket agregado do 5m,
          // preservando o "open" original para manter continuidade visual
          // (fechamento do candle anterior = abertura do candle atual).
          const updated = base.slice()
          const prev = updated[idx]
          updated[idx] = {
            ...prev,
            high,
            low,
            close,
            volume: vol,
          }
          return updated
        }

        // Nenhum candle com esse open_time ainda existe: anexa no final,
        // usando o open calculado a partir do 5m.
        return [
          ...base,
          {
            open_time: openTime,
            open,
            high,
            low,
            close,
            volume: vol,
          },
        ]
      }
    }
  } catch {
    // falha silenciosa: devolve base original
  }

  return base
}

